/**
 * RGM Starter Studio · Phase 1C correction — Touch trim handle.
 *
 * Renders as two thin vertical grip strips on the LEFT and RIGHT
 * edges of a selected clip. Dragging changes only the clip's
 * non-destructive window metadata (`clip_start_ms` / `clip_end_ms`)
 * with live visual feedback. Never modifies the underlying asset.
 */
import React, { useCallback, useRef } from "react";

/**
 * @param {"start"|"end"} edge
 * @param {number} pxPerSecond  timeline scale (px per second)
 * @param {number} clipStartMs  current window start (ms)
 * @param {number} clipEndMs    current window end (ms)
 * @param {number} durationMs   underlying take duration (ms, upper bound)
 * @param {(nextMs:number)=>void} onLivePreview  called during drag with the tentative next value (start OR end)
 * @param {(startMs:number,endMs:number)=>Promise<void>} onCommit  called on release with final window
 */
export default function TrimHandle({
  edge,
  pxPerSecond,
  clipStartMs,
  clipEndMs,
  durationMs,
  onLivePreview,
  onCommit,
}) {
  const dragRef = useRef(null);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.touches?.[0]?.clientX ?? e.clientX;
    dragRef.current = {
      startX,
      origStart: clipStartMs,
      origEnd: clipEndMs,
    };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* */ }
  }, [clipStartMs, clipEndMs]);

  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const x = e.touches?.[0]?.clientX ?? e.clientX;
    const deltaPx = x - dragRef.current.startX;
    const deltaMs = Math.round((deltaPx / pxPerSecond) * 1000);
    const MIN_WIN = 250; // don't let a clip collapse below 250 ms
    if (edge === "start") {
      const next = Math.max(0, Math.min(dragRef.current.origEnd - MIN_WIN, dragRef.current.origStart + deltaMs));
      onLivePreview?.(next);
    } else {
      const upper = durationMs > 0 ? durationMs : (dragRef.current.origEnd + 60000);
      const next = Math.max(dragRef.current.origStart + MIN_WIN, Math.min(upper, dragRef.current.origEnd + deltaMs));
      onLivePreview?.(next);
    }
  }, [edge, pxPerSecond, durationMs, onLivePreview]);

  const onPointerUp = useCallback(async (e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    const x = e.changedTouches?.[0]?.clientX ?? e.clientX;
    const deltaPx = x - dragRef.current.startX;
    const deltaMs = Math.round((deltaPx / pxPerSecond) * 1000);
    const MIN_WIN = 250;
    let ns = dragRef.current.origStart;
    let ne = dragRef.current.origEnd;
    if (edge === "start") {
      ns = Math.max(0, Math.min(dragRef.current.origEnd - MIN_WIN, dragRef.current.origStart + deltaMs));
    } else {
      const upper = durationMs > 0 ? durationMs : (dragRef.current.origEnd + 60000);
      ne = Math.max(dragRef.current.origStart + MIN_WIN, Math.min(upper, dragRef.current.origEnd + deltaMs));
    }
    // Compare against the drag origin: clipStartMs/clipEndMs props already
    // reflect the live preview by now, so comparing to them never commits.
    const { origStart, origEnd } = dragRef.current;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* */ }
    if (ns !== origStart || ne !== origEnd) await onCommit?.(ns, ne);
  }, [edge, pxPerSecond, durationMs, onCommit]);

  const side = edge === "start" ? "-left-3" : "-right-3";
  return (
    <div
      className={`absolute ${side} top-0 bottom-0 w-6 z-20 flex items-center justify-center cursor-ew-resize`}
      style={{ touchAction: "none" }}
      data-testid={`trim-handle-${edge}`}
      role="slider"
      aria-label={`Trim ${edge}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="w-[5px] h-3/5 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.7)] pointer-events-none" />
    </div>
  );
}
